import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { UserFilterDropdown } from '../../components/UserFilterDropdown';
import { AlertDashboard } from '../../components/admin/AlertDashboard';
import { SlideOver } from '../../components/admin/SlideOver';
import { CampaignDetailPanel } from '../../components/admin/CampaignDetailPanel';
import { EmailDebuggerPanel } from '../../components/admin/EmailDebuggerPanel';
import { TabNavigation } from '../../components/admin/TabNavigation';
import {
  Search,
  Mail,
  Calendar,
  Clock,
  RefreshCw,
  Users,
  CheckCircle,
  XCircle,
  FileText,
  Send,
  Layers,
  AlertTriangle
} from 'lucide-react';

interface Campaign {
  id: string;
  user_id: string;
  blog_id: string | null;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'completed' | 'failed';
  scheduled_at: string | null;
  sent_at: string | null;
  total_contacts: number;
  sent_count: number;
  failed_count: number;
  created_at: string;
  user_first_name: string;
  user_email: string;
  user_company_name: string;
  blog_title: string | null;
  success_rate_percent: number;
  failure_rate_percent: number;
  batch_count: number;
  alert_is_failed: boolean;
  alert_is_late_started: boolean;
  alert_has_high_failure_rate: boolean;
  alert_is_stuck_sending: boolean;
  has_alert: boolean;
}

interface AlertSummary {
  alert_type: string;
  alert_count: number;
  alert_severity: string;
  campaign_ids: string[];
}

type TabType = 'scheduled' | 'failed';

export function AdminCampaigns() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authUser, isAdmin, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [alerts, setAlerts] = useState<AlertSummary[]>([]);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('scheduled');
  const [searchQuery, setSearchQuery] = useState('');
  const [alertFilter, setAlertFilter] = useState<string | null>(null);

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [showDebugger, setShowDebugger] = useState(false);
  const [retryingCampaign, setRetryingCampaign] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!authUser) {
        navigate('/admin', { replace: true });
        return;
      }
      if (!isAdmin) {
        navigate('/dashboard', { replace: true });
        return;
      }
      loadData();
    }
  }, [authUser, isAdmin, authLoading, navigate]);

  useEffect(() => {
    const campaignId = searchParams.get('detail');
    const debuggerOpen = searchParams.get('debugger') === 'true';

    if (campaignId) {
      setSelectedCampaignId(campaignId);
      setShowDebugger(debuggerOpen);
    } else {
      setSelectedCampaignId(null);
      setShowDebugger(false);
    }
  }, [searchParams]);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, selectedUserId, activeTab, searchQuery, alertFilter]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (campaigns.some(c => c.status === 'sending')) {
        loadData();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [campaigns]);

  const loadData = async () => {
    try {
      setLoading(true);

      const [campaignsResult, alertsResult] = await Promise.all([
        supabase
          .from('admin_campaign_alerts_view')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase.rpc('get_campaign_alerts_summary')
      ]);

      if (campaignsResult.error) throw campaignsResult.error;
      if (alertsResult.error) throw alertsResult.error;

      setCampaigns(campaignsResult.data || []);
      setAlerts(alertsResult.data || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    let filtered = [...campaigns];

    if (activeTab === 'scheduled') {
      filtered = filtered.filter(c => c.status === 'scheduled' || c.status === 'sending');
    } else if (activeTab === 'failed') {
      filtered = filtered.filter(c => c.status === 'failed');
    }

    if (selectedUserId) {
      filtered = filtered.filter(c => c.user_id === selectedUserId);
    }

    if (alertFilter) {
      filtered = filtered.filter(c => {
        switch (alertFilter) {
          case 'failed':
            return c.alert_is_failed;
          case 'high_failure_rate':
            return c.alert_has_high_failure_rate && !c.alert_is_failed;
          case 'late_started':
            return c.alert_is_late_started;
          case 'stuck_sending':
            return c.alert_is_stuck_sending;
          default:
            return true;
        }
      });
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.subject.toLowerCase().includes(query) ||
        c.user_first_name.toLowerCase().includes(query) ||
        c.user_email.toLowerCase().includes(query) ||
        c.user_company_name.toLowerCase().includes(query) ||
        c.blog_title?.toLowerCase().includes(query)
      );
    }

    setFilteredCampaigns(filtered);
  };

  const handleAlertClick = (alertType: string, campaignIds: string[]) => {
    setAlertFilter(alertFilter === alertType ? null : alertType);
  };

  const handleCampaignClick = (campaignId: string) => {
    setSearchParams({ detail: campaignId });
  };

  const handleClosePanel = () => {
    setSearchParams({});
  };

  const handleOpenDebugger = () => {
    if (selectedCampaignId) {
      setSearchParams({ detail: selectedCampaignId, debugger: 'true' });
    }
  };

  const handleBackToDetail = () => {
    if (selectedCampaignId) {
      setSearchParams({ detail: selectedCampaignId });
    }
  };

  const handleRetryCampaign = async () => {
    if (!selectedCampaignId) return;

    try {
      setRetryingCampaign(true);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retry-failed-campaign-emails`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            campaign_id: selectedCampaignId,
            admin_user_id: authUser?.id
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert(`Retry completed: ${result.succeeded} succeeded, ${result.failed} failed`);
        await loadData();
      } else {
        alert(`Error: ${result.error}`);
      }
    } catch (error) {
      console.error('Error retrying campaign:', error);
      alert('Failed to retry campaign');
    } finally {
      setRetryingCampaign(false);
    }
  };

  const handleCancelCampaign = async () => {
    if (!selectedCampaignId) return;

    if (!confirm('Are you sure you want to cancel this campaign?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('email_campaigns')
        .update({ status: 'draft' })
        .eq('id', selectedCampaignId);

      if (error) throw error;

      alert('Campaign cancelled successfully');
      await loadData();
      handleClosePanel();
    } catch (error) {
      console.error('Error cancelling campaign:', error);
      alert('Failed to cancel campaign');
    }
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaignId) return;

    if (!confirm('Weet je zeker dat je deze campaign wilt verwijderen? Dit kan niet ongedaan worden gemaakt.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('email_campaigns')
        .delete()
        .eq('id', selectedCampaignId);

      if (error) throw error;

      alert('Campaign successfully deleted');
      await loadData();
      handleClosePanel();
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign');
    }
  };

  const getStatusBadge = (status: Campaign['status'], hasAlert: boolean) => {
    const styles = {
      draft: 'bg-orange-50 text-orange-700 border-orange-300 shadow-sm',
      scheduled: 'bg-blue-50 text-blue-700 border-blue-300 shadow-sm',
      sending: 'bg-amber-50 text-amber-700 border-amber-300 shadow-sm animate-pulse',
      completed: 'bg-emerald-50 text-emerald-700 border-emerald-300 shadow-sm',
      failed: 'bg-red-50 text-red-700 border-red-300 shadow-sm',
    };

    const iconColors = {
      draft: 'text-orange-600',
      scheduled: 'text-blue-600',
      sending: 'text-amber-600',
      completed: 'text-emerald-600',
      failed: 'text-red-600',
    };

    const icons = {
      draft: FileText,
      scheduled: Clock,
      sending: Send,
      completed: CheckCircle,
      failed: XCircle,
    };

    const labels = {
      draft: 'Concept',
      scheduled: 'Ingepland',
      sending: 'Bezig...',
      completed: 'Verzonden',
      failed: 'Mislukt',
    };

    const Icon = icons[status];

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        <Icon className={`w-3.5 h-3.5 ${iconColors[status]}`} />
        {labels[status]}
        {hasAlert && status !== 'failed' && (
          <AlertTriangle className="w-3 h-3 ml-0.5 text-amber-600" />
        )}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('nl-NL', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (authLoading || loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  const selectedCampaign = campaigns.find(c => c.id === selectedCampaignId);

  const scheduledCount = campaigns.filter(c => c.status === 'scheduled' || c.status === 'sending').length;
  const failedCount = campaigns.filter(c => c.status === 'failed').length;

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
            <p className="text-sm text-gray-600 mt-1">
              Monitor en beheer alle email campaigns
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="btn-secondary flex items-center gap-2 hover:scale-105 transition-transform"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Vernieuwen
          </button>
        </div>
      </div>

      <AlertDashboard alerts={alerts} onAlertClick={handleAlertClick} />

      <TabNavigation
        tabs={[
          {
            id: 'scheduled',
            label: 'Ingepland',
            icon: Clock,
            count: scheduledCount,
          },
          {
            id: 'failed',
            label: 'Mislukt',
            icon: XCircle,
            count: failedCount,
          },
        ]}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as TabType)}
      />

      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gebruiker</label>
            <UserFilterDropdown
              selectedUserId={selectedUserId}
              onSelectUser={setSelectedUserId}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Zoeken</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Zoek campaigns..."
                className="input pl-10 w-full"
              />
            </div>
          </div>
        </div>

        {alertFilter && (
          <div className={`mt-4 p-3 rounded-lg flex items-center justify-between transition-all ${
            alertFilter === 'failed' ? 'bg-red-50 border border-red-200' :
            alertFilter === 'high_failure_rate' ? 'bg-orange-50 border border-orange-200' :
            alertFilter === 'late_started' ? 'bg-yellow-50 border border-yellow-200' :
            alertFilter === 'stuck_sending' ? 'bg-amber-50 border border-amber-200' :
            'bg-blue-50 border border-blue-200'
          }`}>
            <p className={`text-sm ${
              alertFilter === 'failed' ? 'text-red-800' :
              alertFilter === 'high_failure_rate' ? 'text-orange-800' :
              alertFilter === 'late_started' ? 'text-yellow-800' :
              alertFilter === 'stuck_sending' ? 'text-amber-800' :
              'text-blue-800'
            }`}>
              Filtered by alert type: <span className="font-medium">{alertFilter}</span>
            </p>
            <button
              onClick={() => setAlertFilter(null)}
              className={`text-sm font-medium transition-colors ${
                alertFilter === 'failed' ? 'text-red-600 hover:text-red-700' :
                alertFilter === 'high_failure_rate' ? 'text-orange-600 hover:text-orange-700' :
                alertFilter === 'late_started' ? 'text-yellow-600 hover:text-yellow-700' :
                alertFilter === 'stuck_sending' ? 'text-amber-600 hover:text-amber-700' :
                'text-blue-600 hover:text-blue-700'
              }`}
            >
              Clear filter
            </button>
          </div>
        )}
      </div>

      {filteredCampaigns.length === 0 ? (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg text-center py-16">
          <div className="bg-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
            <Mail className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Geen campaigns gevonden
          </h2>
          <p className="text-gray-600">
            Probeer andere filters of zoektermen
          </p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Gebruiker
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="text-center py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-right py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Verzonden
                  </th>
                  <th className="text-center py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Batches
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Datum
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredCampaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    onClick={() => handleCampaignClick(campaign.id)}
                    className="hover:bg-blue-50 cursor-pointer transition-all duration-150 hover:shadow-sm"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">
                            {campaign.user_first_name}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {campaign.user_company_name || campaign.user_email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="max-w-xs">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {campaign.subject}
                        </div>
                        {campaign.blog_title && (
                          <div className="text-xs text-gray-500 truncate">
                            Blog: {campaign.blog_title}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {getStatusBadge(campaign.status, campaign.has_alert)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="text-sm">
                        <span className="font-semibold text-gray-900">{campaign.sent_count}</span>
                        <span className="text-gray-400"> / </span>
                        <span className="text-gray-600">{campaign.total_contacts}</span>
                      </div>
                      {campaign.total_contacts > 0 && (
                        <div className={`text-xs font-medium ${
                          campaign.success_rate_percent >= 98 ? 'text-emerald-600' :
                          campaign.success_rate_percent >= 95 ? 'text-green-600' :
                          campaign.success_rate_percent >= 90 ? 'text-yellow-600' :
                          campaign.success_rate_percent >= 80 ? 'text-orange-600' :
                          'text-red-600'
                        }`}>
                          {campaign.success_rate_percent}% succes
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {campaign.batch_count > 0 ? (
                        <div className="flex items-center justify-center gap-1">
                          <Layers className={`w-3.5 h-3.5 ${
                            campaign.batch_count >= 10 ? 'text-emerald-500' :
                            campaign.batch_count >= 5 ? 'text-blue-500' :
                            'text-gray-400'
                          }`} />
                          <span className={`text-sm font-medium ${
                            campaign.batch_count >= 10 ? 'text-emerald-700' :
                            campaign.batch_count >= 5 ? 'text-blue-700' :
                            'text-gray-700'
                          }`}>
                            {campaign.batch_count}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm text-gray-700">
                        {campaign.status === 'scheduled' && campaign.scheduled_at && (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            <span className="whitespace-nowrap">{formatDate(campaign.scheduled_at)}</span>
                          </div>
                        )}
                        {(campaign.status === 'completed' || campaign.status === 'failed' || campaign.status === 'sending') && campaign.sent_at && (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            <span className="whitespace-nowrap">{formatDate(campaign.sent_at)}</span>
                          </div>
                        )}
                        {!campaign.scheduled_at && !campaign.sent_at && (
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="whitespace-nowrap">{formatDate(campaign.created_at)}</span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={!!selectedCampaignId && !showDebugger}
        onClose={handleClosePanel}
        title={selectedCampaign?.subject || 'Campaign Details'}
        width="wide"
      >
        {selectedCampaignId && !showDebugger && (
          <CampaignDetailPanel
            campaignId={selectedCampaignId}
            onClose={handleClosePanel}
            onOpenDebugger={handleOpenDebugger}
            onRetry={handleRetryCampaign}
            onCancel={handleCancelCampaign}
            onDelete={handleDeleteCampaign}
          />
        )}
      </SlideOver>

      <SlideOver
        isOpen={!!selectedCampaignId && showDebugger}
        onClose={handleClosePanel}
        title="Email Debugger"
        width="wide"
      >
        {selectedCampaignId && showDebugger && selectedCampaign && (
          <EmailDebuggerPanel
            campaignId={selectedCampaignId}
            campaignSubject={selectedCampaign.subject}
            onBack={handleBackToDetail}
          />
        )}
      </SlideOver>
    </AdminLayout>
  );
}
